 import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
 import { WorkflowData, Answer, AnswerType, LatticeFile, QuestionGroup } from '@/types/latticeTypes'
import { FilePage } from '@/types/FilePages';

interface UpdateWorkflowNameArgs {
    workflowId: number;
    name: string;
}

interface GetUploadLinkResponse {
    uploadLink: string;
    id: number;
}

interface DeleteQuestionArgs {
    workflowId: number;
    questionId: number;
}

interface GetAnswersArgs {
    workflowId: number;
}

interface CreateWorkflowFileArgs {
    workflowId: number;
    latticeFileId: number;
}

interface DeleteWorkflowFileArgs {
    workflowFileId: number;
}

interface CreateQuestionArgs {
    workflowId: number;
    parameter: string;
    answerTypes: AnswerType[];
    content: string;
}

interface UpdateQuestionArgs {
    questionId: number;
    parameter: string;
    answerTypes: AnswerType[];
    content: string;
}

interface UpdateQuestionGroupArgs {
    groupId: number;
    collapsed: boolean;
}

interface MoveQuestionGroupArgs {
    id: number;
    previousId?: number;
    nextId?: number;
}

type GroupQuestionsArgs = number[];

type GroupWorkflowFileGroupsArgs = number[];

interface UpdateWorkflowFileGroupArgs {
    groupId: number;
    collapseState: 'EXPANDED' | 'ZERO' | 'ONE';
}

interface MoveWorkflowFileGroupArgs {
    id: number;
    previousId?: number;
    nextId?: number;
}

interface ExportWorkflowArgs {
    workflowId: number;
    workflowName: string;
}

interface ReorderArgs {
    id: number;
    previousId?: number;
    nextId?: number;
}

interface UpdateFileNameArgs {
    latticeFileId: number;
    fileName: string;
}

interface RateAnswerArgs {
    answerId: number;
    content: string;
    page: number;
}

interface DeleteAnswerRatingArgs {
    ratingId: number;
}

interface UpdateAnswerClickedAtArgs {
    id: number;
    clickedAt: string;
}

export const latticeApi = createApi({
    reducerPath: 'latticeApi',
    baseQuery: fetchBaseQuery({
        baseUrl: 'https://api.getspect.ai/v3',
        credentials: 'include'
    }),
    tagTypes: ['Workflow', 'Question', 'Document', 'Answer', 'FilePage'],
    endpoints: (builder) => ({
        getWorkflows: builder.query<WorkflowData[], void>({
            query: () => '/workflow',
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: 'Workflow' as const, id })),
                        'Workflow'
                    ]
                    : ['Workflow']
        }),

        createWorkflow: builder.mutation<number, string>({
            query: (name) => ({
                url: '/workflow',
                method: 'POST',
                headers: {
                    'Content-Type': 'text/plain',
                },
                body: name
            }),
            invalidatesTags: ['Workflow']
        }),

        updateWorkflowName: builder.mutation<void, UpdateWorkflowNameArgs>({
            query: ({ workflowId, name }) => ({
                url: `/workflow/${workflowId}`,
                method: 'PUT',
                body: { name }
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Workflow', id: arg.workflowId }]
        }),

        deleteWorkflow: builder.mutation<void, number>({
            query: (workflowId) => ({
                url: `/workflow/${workflowId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Workflow']
        }),

        getUploadLink: builder.mutation<GetUploadLinkResponse, string>({
            query: (fileName) => ({
                url: '/lattice-file/get-upload-link',
                method: 'POST',
                body: fileName,
                headers: {
                    'Content-Type': 'text/plain',
                },
            })
        }),

        notifyFileUploaded: builder.mutation<void, number>({
            query: (fileId) => ({
                url: `/lattice-file/${fileId}/notify-uploaded`,
                method: 'POST'
            }),
            invalidatesTags: ['Workflow']
        }),

        createWorkflowFile: builder.mutation<void, CreateWorkflowFileArgs>({
            query: (args) => ({
                url: '/workflow-file',
                method: 'POST',
                body: args
            }),
        }),

        createQuestion: builder.mutation<QuestionGroup, CreateQuestionArgs>({
            query: ({ workflowId, ...body }) => ({
                url: `/workflow/${workflowId}/question`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_result, _error, { workflowId }) => [{ type: 'Workflow', id: workflowId }]
        }),

        updateQuestion: builder.mutation<void, UpdateQuestionArgs>({
            query: ({ questionId, ...body }) => ({
                url: `/question/${questionId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['Workflow']
        }),

        deleteQuestion: builder.mutation<void, DeleteQuestionArgs>({
            query: ({ questionId }) => ({
                url: `/question/${questionId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_result, _error, { workflowId }) => [
                { type: 'Workflow', id: workflowId }
            ]
        }),

        groupQuestions: builder.mutation<void, GroupQuestionsArgs>({
            query: (questionIds) => ({
                url: '/question-group/group',
                method: 'POST',
                body: questionIds
            }),
            invalidatesTags: ['Workflow']
        }),

        updateQuestionGroup: builder.mutation<void, UpdateQuestionGroupArgs>({
            query: ({ groupId, collapsed }) => ({
                url: `/question-group/${groupId}`,
                method: 'PUT',
                body: { collapsed }
            }),
            invalidatesTags: ['Workflow']
        }),

        moveQuestionGroup: builder.mutation<void, MoveQuestionGroupArgs>({
            query: ({ id, nextId, previousId }) => ({
                url: '/question-group/move',
                method: 'POST',
                body: { id, nextId, previousId }
            }),
            invalidatesTags: ['Workflow']
        }),

        getLatticeFile: builder.query<LatticeFile, number>({
            query: (fileId) => `/lattice-file/${fileId}`,
        }),

        getAllLatticeFiles: builder.query<LatticeFile[], void>({
            query: () => '/lattice-file',
            providesTags: ['Document']
        }),

        deleteWorkflowFile: builder.mutation<void, DeleteWorkflowFileArgs>({
            query: ({ workflowFileId }) => ({
                url: `/workflow-file/${workflowFileId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Workflow']
        }),

        getAnswers: builder.query<Answer[], GetAnswersArgs>({
            query: ({ workflowId }) => `/answer?workflowId=${workflowId}`,
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: 'Answer' as const, id })),
                        'Answer'
                    ]
                    : ['Answer']
        }),

        getFilePages: builder.query<FilePage[], number>({
            query: (fileId) => ({
                url: `/lattice-file/${fileId}/pdf`
            }),
            providesTags: (result, _error, _arg) =>
                result
                  ? [...result.map(({ id }) => ({ type: 'FilePage' as const, id })), 'FilePage']
                  : ['FilePage'],
        }),

        getWorkflowFilePages: builder.query<FilePage[], number>({
            query: (workflowFileId) => ({
                url: `/workflow-file/${workflowFileId}/pdf`
            }),
            providesTags: (result, _error, _arg) =>
                result
                  ? [...result.map(({ id }) => ({ type: 'FilePage' as const, id })), 'FilePage']
                  : ['FilePage'],
        }),

        exportWorkflow: builder.mutation<void, ExportWorkflowArgs>({
            query: ({ workflowId, workflowName }) => ({
                url: `/workflow/${workflowId}/csv`,
                responseHandler: async (response) => {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${workflowName}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                },
            }),
        }),

        duplicateWorkflow: builder.mutation<WorkflowData, number>({
            query: (workflowId) => ({
                url: `/workflow/${workflowId}/duplicate`,
                method: 'POST'
            }),
            invalidatesTags: ['Workflow']
        }),

        reorderWorkflowFile: builder.mutation<void, ReorderArgs>({
            query: (args) => ({
                url: '/workflow-file/move',
                method: 'POST',
                body: args
            }),
            invalidatesTags: ['Workflow']
        }),

        groupWorkflowFileGroups: builder.mutation<void, GroupWorkflowFileGroupsArgs>({
            query: (workflowFileGroupIds) => ({
                url: '/workflow-file-group/group',
                method: 'POST',
                body: workflowFileGroupIds
            }),
            invalidatesTags: ['Workflow']
        }),

        updateWorkflowFileGroup: builder.mutation<void, UpdateWorkflowFileGroupArgs>({
            query: ({ groupId, collapseState }) => ({
                url: `/workflow-file-group/${groupId}`,
                method: 'PUT',
                body: { collapseState }
            }),
            invalidatesTags: ['Workflow']
        }),

        moveWorkflowFileGroup: builder.mutation<void, MoveWorkflowFileGroupArgs>({
            query: ({ id, nextId, previousId }) => ({
                url: '/workflow-file-group/move',
                method: 'POST',
                body: { id, nextId, previousId }
            }),
            invalidatesTags: ['Workflow']
        }),

        reoderQuestion: builder.mutation<void, ReorderArgs>({
            query: (args) => ({
                url: '/question/move',
                method: 'POST',
                body: args
            }),
            invalidatesTags: ['Workflow']
        }),

        updateFileName: builder.mutation<void, UpdateFileNameArgs>({
            query: ({ latticeFileId, fileName }) => ({
                url: `/lattice-file/${latticeFileId}`,
                method: 'PUT',
                body: { fileName }
            }),
            invalidatesTags: ['Workflow']
        }),

        rateAnswer: builder.mutation<void, RateAnswerArgs>({
            query: ({ answerId, content, page }) => ({
                url: `/answer/${answerId}/rate`,
                method: 'POST',
                body: { content, page }
            }),
            invalidatesTags: (_result, _error, { answerId }) => [
                { type: 'Answer', id: answerId },
                'Answer'
            ]
        }),

        deleteAnswerRating: builder.mutation<void, DeleteAnswerRatingArgs>({
            query: ({ ratingId }) => ({
                url: `/answer-rating/${ratingId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Answer']
        }),

        updateAnswerClickedAt: builder.mutation<void, UpdateAnswerClickedAtArgs>({
            query: ({ id, clickedAt }) => ({
                url: '/answer',
                method: 'PUT',
                body: { id, clickedAt }
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Answer', id },
                'Answer'
            ]
        }),
    })
})

export const {
    useGetWorkflowsQuery,
    useCreateWorkflowMutation,
    useUpdateWorkflowNameMutation,
    useDeleteWorkflowMutation,
    useDeleteQuestionMutation,
    useGetUploadLinkMutation,
    useNotifyFileUploadedMutation,
    useCreateWorkflowFileMutation,
    useGetLatticeFileQuery,
    useDeleteWorkflowFileMutation,
    useGetAnswersQuery,
    useCreateQuestionMutation,
    useUpdateQuestionMutation,
    useGroupQuestionsMutation,
    useUpdateQuestionGroupMutation,
    useMoveQuestionGroupMutation,
    useGetAllLatticeFilesQuery,
    useGetFilePagesQuery,
    useGetWorkflowFilePagesQuery,
    useExportWorkflowMutation,
    useDuplicateWorkflowMutation,
    useReorderWorkflowFileMutation,
    useReoderQuestionMutation,
    useUpdateFileNameMutation,
    useRateAnswerMutation,
    useDeleteAnswerRatingMutation,
    useGroupWorkflowFileGroupsMutation,
    useUpdateWorkflowFileGroupMutation,
    useMoveWorkflowFileGroupMutation,
    useUpdateAnswerClickedAtMutation,
} = latticeApi
